import React, { useEffect } from 'react'
import { Building2, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import { useCompanyProfile } from '../hooks/useCompanyProfile'
import { subscribeToProfileCompletionChanges } from '../companyProfileCompletionBus'

// Persistent (non-dismissible) nudge shown above the dashboard while the
// workspace's Company Profile (Identity & Positioning) is incomplete. Unlike
// UsageWarningBanner, this never goes away on its own — it disappears only
// once the profile is actually completed. Kept deliberately minimal — a
// small accent-tinted pill, not a full-width bar — so it reads as a nudge,
// not an alarm.
export const CompanyProfileBanner: React.FC = () => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()
  const { workspace, isComplete, identityComplete, loading, reload } = useCompanyProfile()

  // BUG-015: this instance's own state doesn't know when the Settings page's
  // separate useCompanyProfile() instance just saved a completed profile —
  // recheck in place instead of waiting for this component to remount.
  useEffect(() => subscribeToProfileCompletionChanges(reload), [reload])

  if (loading || !workspace || isComplete) return null

  const isOwner = workspace.role === 'owner'
  // Identity is done but no complete ICP exists: the gap is the ICP, which owners and
  // admins both manage (Sales → ICPs) — so the nudge goes to both.
  const missingIcpOnly = identityComplete
  const canFix = missingIcpOnly ? workspace.role === 'owner' || workspace.role === 'admin' : isOwner

  return (
    <div
      className="shrink-0 flex items-center justify-center gap-2 py-1.5"
      style={{ backgroundColor: `${t.accent}14` }}
      role="status"
    >
      <Building2 size={14} className="shrink-0" style={{ color: t.accent }} />
      <span className="text-sm font-medium" style={{ color: t.text }}>
        {missingIcpOnly
          ? canFix
            ? 'Add your ideal customer profile so Zeph knows who to target.'
            : "Your workspace hasn't set up its ideal customer profile yet."
          : isOwner
            ? 'Set up your company profile so Zeph can work at full capacity.'
            : "Your workspace admin hasn't finished the company profile yet."}
      </span>
      {canFix && (
        <button
          type="button"
          onClick={() => navigate(missingIcpOnly ? '/dashboard/sales?tab=icps' : '/dashboard/settings?tab=company-profile')}
          className="flex items-center gap-0.5 text-sm font-semibold transition hover:opacity-70"
          style={{ color: t.accent }}
        >
          Complete
          <ArrowRight size={12} />
        </button>
      )}
    </div>
  )
}

export default React.memo(CompanyProfileBanner)
